import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import sitemateLogoAsset from "@/assets/sitemate-logo.png.asset.json";
const sitemateLogoSrc = sitemateLogoAsset.url;

interface SuccessMarketingBannerProps {
  heading1: string | undefined;
  heading2: string | undefined;
  imageUrl: string | undefined;
  ctaLabel: string | undefined;
  ctaUrl: string | undefined;
  generatorSlug: string;
  isDevMode: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
}

function buildCtaUrl(baseUrl: string, generatorSlug: string): string {
  const separator = baseUrl.includes("?") ? "&" : "?";
  return `${baseUrl}${separator}utm_source=${generatorSlug}&utm_medium=generator&utm_campaign=generator_success_modal&utm_content=cta_button`;
}

export function SuccessMarketingBanner({
  heading1,
  heading2,
  imageUrl,
  ctaLabel,
  ctaUrl,
  generatorSlug,
  isDevMode,
  onCollapseChange,
}: SuccessMarketingBannerProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const resolvedHeading1 = heading1?.trim();
  const resolvedHeading2 = heading2?.trim();
  const resolvedImageUrl = imageUrl?.trim();
  const resolvedCtaLabel = ctaLabel?.trim() || "Try Sitemate Free";
  const resolvedCtaUrl = ctaUrl?.trim() || "https://sitemate.com/software/sitemate/";

  const hasAllFields = Boolean(
    resolvedHeading1 &&
    resolvedHeading2 &&
    resolvedImageUrl &&
    resolvedCtaUrl
  );
  const isDevPreview = isDevMode && !hasAllFields;

  // In production, don't render if fields are missing
  if (!isDevMode && !hasAllFields) return null;

  const toggleCollapse = () => {
    const next = !isCollapsed;
    setIsCollapsed(next);
    onCollapseChange?.(next);
  };

  if (isCollapsed) {
    return (
      <button
        onClick={toggleCollapse}
        className="flex items-center justify-center w-8 h-full bg-blue-50 rounded-l-lg border-r border-border hover:bg-blue-100 transition-colors"
        aria-label="Expand marketing banner"
      >
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </button>
    );
  }

  const displayHeading1 = resolvedHeading1 || "{heading1}";
  const displayHeading2 = resolvedHeading2 || "{heading2}";
  const displayCtaLabel = resolvedCtaLabel;
  const displayCtaUrl = buildCtaUrl(resolvedCtaUrl, generatorSlug);

  return (
    <div
      className={`relative flex flex-col justify-between gap-4 p-5 bg-blue-50 rounded-l-lg w-1/2 shrink-0 ${
        isDevPreview ? "border-2 border-dashed border-blue-300" : ""
      }`}
    >
      {/* Dev mode collapse toggle */}
      {isDevMode && (
        <button
          onClick={toggleCollapse}
          className="absolute top-2 right-2 p-1 rounded hover:bg-blue-100 transition-colors"
          aria-label="Collapse marketing banner"
        >
          <ChevronLeft className="h-4 w-4 text-muted-foreground" />
        </button>
      )}

      {/* Top: Sitemate logo */}
      <div className="flex items-center justify-center">
        <img
          src={sitemateLogoSrc}
          alt="Sitemate"
          className="h-12 object-contain"
        />
      </div>

      {/* Headings */}
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-foreground">
          {displayHeading1}
        </h3>
        <h3 className="text-lg font-semibold text-foreground">
          {displayHeading2}
        </h3>
      </div>

      {/* Marketing image */}
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="Product preview"
          className="w-full rounded-md object-contain max-h-36"
        />
      ) : (
        <div className="w-full h-28 rounded-md bg-blue-100 flex items-center justify-center">
          <span className="text-xs text-muted-foreground">{"{image}"}</span>
        </div>
      )}

      {/* Bottom section: CTA */}
      <div className="mt-auto">
        <Button
          asChild={!isDevPreview}
          disabled={isDevPreview}
          className="w-full h-11"
          size="lg"
        >
          {isDevPreview ? (
            <span>{displayCtaLabel}</span>
          ) : (
            <a href={displayCtaUrl} target="_blank" rel="noopener noreferrer">
              {displayCtaLabel}
            </a>
          )}
        </Button>
      </div>
    </div>
  );
}
